import { useEffect, useRef } from "react";

// The page-level failure panel. It replaces the "Cargando…" that used to
// stay on screen forever when a query failed (the loading branch ran
// before the error branch, and a failed query never sets `data`).
//
// Focus moves to Reintentar on mount: the panel is role="alert", so a
// screen reader announces it, and the only thing a user can do next is
// retry — putting the keyboard there saves them the hunt. Only if the
// user hasn't engaged anything yet (focus still on <body>): a failure
// that lands late must not yank focus out of a field mid-typing. A
// section-level error passes `autoFocus={false}` and never moves it.
type Props = {
  title: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
  autoFocus?: boolean;
};

export function PageError({
  title,
  description,
  onRetry,
  retryLabel = "Reintentar",
  autoFocus = true,
}: Props) {
  const retryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!autoFocus) return;
    const active = document.activeElement;
    if (active === null || active === document.body) retryRef.current?.focus();
    // Mount-only: a re-render must not re-grab focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="empty" role="alert">
      <div className="empty__title">{title}</div>
      {description && <div className="empty__sub">{description}</div>}
      {onRetry && (
        <button
          ref={retryRef}
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={onRetry}
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
}
